// components/onboarding/steps/DocumentsStep.jsx - World-Class Enterprise UI
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DocumentUploader from '../DocumentUploader';

const DocumentsStep = ({ documents, setDocuments, onNext, onBack, formData }) => {
  const [errors, setErrors] = useState({});

  const handleFileChange = (documentType, file) => {
    setDocuments({ ...documents, [documentType]: file });
    if (errors[documentType]) {
      setErrors({ ...errors, [documentType]: null });
    }
  };

  const validateDocuments = () => {
    const newErrors = {};
    
    // Check which KYC documents are required based on form data
    const hasAadhaar = formData.aadhaar_number?.trim() && formData.aadhaar_number !== '********';
    const hasPAN = formData.pan_number?.trim() && formData.pan_number !== '********';
    const hasGST = formData.gst_number?.trim() && formData.gst_number !== '********';
    
    // At least one KYC number must be provided (this should be validated in previous step)
    if (!hasAadhaar && !hasPAN && !hasGST) {
      newErrors['aadhaar-front'] = 'At least one KYC document is required (Aadhaar, PAN, or GST)';
      setErrors(newErrors);
      return false;
    }
    
    // Check if at least one set of documents is uploaded
    const hasAadhaarDocs = documents['aadhaar-front'] && documents['aadhaar-back'];
    const hasPANDoc = documents.pan;
    
    // If Aadhaar number is provided, require Aadhaar documents
    if (hasAadhaar) {
      if (!documents['aadhaar-front']) {
        newErrors['aadhaar-front'] = 'Aadhaar front document required';
      }
      if (!documents['aadhaar-back']) {
        newErrors['aadhaar-back'] = 'Aadhaar back document required';
      }
    }
    
    // If PAN number is provided, require PAN document only if Aadhaar documents are not uploaded
    // This allows users to provide multiple KYC numbers but upload documents for just one
    if (hasPAN && !hasPANDoc && !hasAadhaarDocs) {
      newErrors.pan = 'PAN document required (or upload Aadhaar documents)';
    }
    
    // At least one complete set of documents must be uploaded
    if (!hasAadhaarDocs && !hasPANDoc) {
      if (hasAadhaar) {
        newErrors['aadhaar-front'] = 'Please upload Aadhaar documents';
      } else if (hasPAN) {
        newErrors.pan = 'Please upload PAN document';
      }
    }
    
    // GST certificate is optional even if GST number is provided
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateDocuments()) onNext();
  };

  const uploadedCount = Object.values(documents).filter(Boolean).length;
  const totalCount = Object.keys(documents).length;
  const progress = (uploadedCount / totalCount) * 100;

  const hasPAN = formData.pan_number && formData.pan_number !== '********';
  const hasGST = formData.gst_number && formData.gst_number !== '********';

  const benefits = [
    {
      icon: '⚡',
      title: 'Instant Verification',
      description: 'Get verified within 24 hours with complete documentation'
    },
    {
      icon: '🔒',
      title: 'Secure Upload',
      description: 'Bank-grade encryption protects all your documents'
    },
    {
      icon: '✅',
      title: 'One-Time Process',
      description: 'Upload once, ship forever. No repeated verifications'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="w-full max-w-7xl mx-auto"
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: Documents Upload */}
        <div className="lg:col-span-3">
          <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-transparent to-purple-50/50 pointer-events-none" />
            
            <div className="relative p-8 md:p-12">
              {/* Header */}
              <div className="mb-8">
                <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-3">
                  Document Verification
                </h2>
                <p className="text-lg text-gray-600">
                  Upload clear copies for quick verification
                </p>
              </div>

              {/* Progress Card */}
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <div className="font-bold text-lg">Upload Progress</div>
                      <div className="text-blue-100 text-sm">{uploadedCount} of {totalCount} documents uploaded</div>
                    </div>
                  </div>
                  <div className="text-3xl font-bold">{Math.round(progress)}%</div>
                </div>
                <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.5 }}
                    className="h-full bg-white rounded-full relative overflow-hidden"
                  >
                    <motion.div
                      animate={{ x: ['0%', '200%'] }}
                      transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent"
                    />
                  </motion.div>
                </div>
              </motion.div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Mandatory Documents */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-3 border-b-2 border-red-100">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-600 to-rose-600 flex items-center justify-center text-white">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Mandatory Documents</h3>
                  </div>

                  <DocumentUploader
                    documentType="aadhaar-front"
                    label="Aadhaar Card - Front"
                    required
                    file={documents['aadhaar-front']}
                    onFileChange={handleFileChange}
                    description="Upload front side with photo visible"
                  />
                  {errors['aadhaar-front'] && <p className="text-sm text-red-600 -mt-2">{errors['aadhaar-front']}</p>}

                  <DocumentUploader
                    documentType="aadhaar-back"
                    label="Aadhaar Card - Back"
                    required
                    file={documents['aadhaar-back']}
                    onFileChange={handleFileChange}
                    description="Upload back side with address"
                  />
                  {errors['aadhaar-back'] && <p className="text-sm text-red-600 -mt-2">{errors['aadhaar-back']}</p>}

                  {hasPAN && (
                    <>
                      <DocumentUploader
                        documentType="pan"
                        label="PAN Card"
                        required
                        file={documents.pan}
                        onFileChange={handleFileChange}
                        description="Upload your PAN card"
                      />
                      {errors.pan && <p className="text-sm text-red-600 -mt-2">{errors.pan}</p>}
                    </>
                  )}
                </div>

                {/* Optional Documents */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-3 border-b-2 border-blue-100">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Optional Documents</h3>
                    <span className="ml-auto text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold">Recommended</span>
                  </div>

                  <DocumentUploader
                    documentType="gst-certificate"
                    label="GST Certificate"
                    file={documents['gst-certificate']}
                    onFileChange={handleFileChange}
                    description="Upload your GST registration certificate (speeds up verification)"
                  />

                  <DocumentUploader
                    documentType="coi"
                    label="Certificate of Incorporation (COI)"
                    file={documents.coi}
                    onFileChange={handleFileChange}
                    description="Company incorporation certificate (for registered companies)"
                  />

                  <DocumentUploader
                    documentType="cheque"
                    label="Cancelled Cheque"
                    file={documents.cheque}
                    onFileChange={handleFileChange}
                    description="For COD remittance setup (account verification)"
                  />

                  <DocumentUploader
                    documentType="msme"
                    label="MSME Certificate"
                    file={documents.msme}
                    onFileChange={handleFileChange}
                    description="Unlock special MSME benefits and discounts"
                  />
                </div>

                {/* Brand Assets */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-3 border-b-2 border-purple-100">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center text-white">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Brand Assets</h3>
                    <span className="ml-auto text-xs px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-semibold">Optional</span>
                  </div>

                  <DocumentUploader
                    documentType="logo"
                    label="Brand Logo"
                    file={documents.logo}
                    onFileChange={handleFileChange}
                    description="Upload your brand logo (JPG or PNG only, max 5MB)"
                  />
                </div>

                {/* Navigation */}
                <div className="flex gap-4 pt-6">
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    type="button"
                    onClick={onBack}
                    className="flex-1 px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all"
                  >
                    Back
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    type="submit"
                    className="flex-1 px-8 py-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
                  >
                    Continue to Review
                  </motion.button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Right: Benefits & Tips */}
        <div className="lg:col-span-2">
          <div className="sticky top-8 space-y-6">
            {/* Why Upload Documents */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 border border-gray-100"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why We Need These</h3>
              <div className="space-y-4">
                {benefits.map((benefit, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-all"
                  >
                    <div className="flex-shrink-0 text-3xl">{benefit.icon}</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{benefit.title}</h4>
                      <p className="text-sm text-gray-600">{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Upload Tips */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Quick Tips</h4>
                  <p className="text-sm text-gray-700">For faster verification</p>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Use clear, well-lit photos</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>All text should be readable</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Documents should not be expired</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 font-bold">✓</span>
                  <span>Max 5MB per file (JPG, PNG, PDF)</span>
                </li>
              </ul>
            </motion.div>

            {/* Illustration */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="relative rounded-2xl overflow-hidden"
            >
              <img
                src="/person_with_mobile.png"
                alt="Document verification"
                className="w-full h-auto object-contain"
                onError={(e) => {
                  e.target.src = '/person_with_box.png';
                }}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DocumentsStep;
